import { useState } from "react";
import { siteName } from "@/mocks/editorial";

const FORM_URL = "https://readdy.ai/api/form/dadqdfjep053ijub1420";

type Status = "idle" | "sending" | "success" | "error";

export default function ContactSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾：蜜罐被填入则静默视为成功，不提交
    const honey = (fd.get("website_alt") as string | null)?.trim() ?? "";
    if (honey) {
      setStatus("success");
      setMessage("已收到你的来信，我们会尽快回复。");
      form.reset();
      return;
    }

    setStatus("sending");
    setMessage("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>),
      });
      const responseText = await res.text();
      let code = "";
      let serverMsg = "";
      try {
        const parsed = JSON.parse(responseText);
        code = parsed?.code ?? "";
        serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          "";
      } catch {
        code = "";
      }
      if (res.ok && code === "OK") {
        setStatus("success");
        setMessage("订阅成功，感谢关注，最新观察会第一时间送达你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          serverMsg || "提交失败，请稍后重试或换一种方式联系我们。"
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络出了点问题，请稍后再试。");
    }
  };

  return (
    <section id="contact" className="scroll-mt-24 bg-foreground-950 py-12 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          {/* 左：说明与联系 */}
          <div className="lg:col-span-6">
            <p className="inline-flex items-center gap-2 rounded-full bg-background-100/10 px-3 py-1 text-xs font-medium tracking-wide text-accent-300">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent-500"></span>
              订阅与咨询
            </p>
            <h2 className="mt-4 font-heading text-2xl font-semibold leading-snug text-background-50 md:text-[2rem] md:leading-[1.3]">
              每周一封，聊透正在震荡的热点
            </h2>
            <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-background-200/90">
              留下邮箱，我们会定期整理值得关注的热点传播样本与观察解读发给你——不刷屏、不追热点，只发经得起思考的内容。
            </p>

            <ul className="mt-8 space-y-4">
              <li className="flex items-center gap-3 text-[15px] text-background-100">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-background-100/10 text-accent-300">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                商务与线索建议：guabao-observe@example.com
              </li>
              <li className="flex items-center gap-3 text-[15px] text-background-100">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-background-100/10 text-accent-300">
                  <i className="ri-chat-3-line text-lg"></i>
                </span>
                一起拆解一场风波：带上线索，我们聊聊它的结构
              </li>
            </ul>

            <p className="mt-8 border-t border-background-100/10 pt-6 text-sm leading-relaxed text-background-200/70">
              收到来信我们都会认真看。出于安全与筛选，个别过于激进、缺乏信源或涉嫌人身攻击的讨论，我们可能不便回应——理性讨论，是我们共同的基本盘。
            </p>
          </div>

          {/* 右：表单卡片 */}
          <div className="lg:col-span-6">
            <form
              onSubmit={submit}
              data-readdy-form
              className="rounded-2xl bg-background-50 p-6 md:p-8"
              aria-label="订阅瓜爆观察"
            >
              <h3 className="font-heading text-xl font-semibold text-foreground-950">
                订阅 {siteName}
              </h3>
              <p className="mt-1 text-sm text-foreground-600">
                欢迎留下邮箱，也欢迎补充一两句你想聊的方向。
              </p>

              <div className="mt-6 space-y-4">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-foreground-800">
                    你的邮箱 <span className="text-primary-600">*</span>
                  </span>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="name@example.com"
                    className="w-full rounded-xl border border-background-300 bg-background-100/60 px-4 py-3 text-[15px] text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                  />
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-foreground-800">
                    想聊的方向（选填）
                  </span>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="例如：最近某场风波为什么能持续发酵这么久？"
                    className="w-full resize-none rounded-xl border border-background-300 bg-background-100/60 px-4 py-3 text-[15px] text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                  ></textarea>
                </label>

                {/* 反垃圾蜜罐 */}
                <label className="website-channel">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </label>

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-[15px] font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "sending" ? "提交中…" : "订阅，不错过观察"}
                  <i className="ri-send-plane-line"></i>
                </button>
              </div>

              {status === "success" && (
                <p className="mt-4 rounded-xl bg-secondary-100 px-4 py-3 text-sm text-secondary-800">
                  <i className="ri-checkbox-circle-line mr-1"></i>
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="mt-4 rounded-xl bg-primary-100 px-4 py-3 text-sm text-primary-700">
                  <i className="ri-error-warning-line mr-1"></i>
                  {message}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}